import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, UserIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { useHeader } from '../../contexts/HeaderContext';
import { useStudentsList } from '../../hooks/useStudentsList';
import { Attendance, AttendanceRecord, getAttendance, updateStudentSignIn, updateStudentSignOut, batchSignInStudents } from '../../services/attendanceService';
import StudentAttendanceCard from './StudentAttendanceCard';
import AttendanceDialog from './AttendanceDialog';
import AttendanceBatchDialog from './AttendanceBatchDialog';

// 导出按钮样式
const exportButtonStyles = `
  .export-tooltip-container {
    position: relative;
  }
  .export-tooltip-text {
    visibility: hidden;
    position: absolute;
    z-index: 100;
    background-color: rgba(0, 0, 0, 0.8);
    color: white;
    text-align: center;
    padding: 5px 10px;
    border-radius: 6px;
    bottom: -30px;
    left: 50%;
    transform: translateX(-50%);
    white-space: nowrap;
  }
  .export-tooltip-container:hover .export-tooltip-text {
    visibility: visible;
  }
`;

const AttendancePage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const courseId = queryParams.get('courseId');
    const attendanceId = queryParams.get('id');
    const dateParam = queryParams.get('date');
    const programIdParam = queryParams.get('programId');
    const { t, i18n } = useTranslation();
    const { setHeaderTheme } = useHeader();

    // 添加CSS样式到文档头
    useEffect(() => {
        // 创建样式标签
        const styleElement = document.createElement('style');
        styleElement.textContent = exportButtonStyles;
        document.head.appendChild(styleElement);

        // 清理函数
        return () => {
            document.head.removeChild(styleElement);
        };
    }, []);

    // 处理日期参数 - 使用useMemo避免不必要的重复计算
    const selectedDate = useMemo(() => {
        if (dateParam) {
            // 将时间戳转换为日期对象
            try {
                const timestamp = parseInt(dateParam, 10);
                if (!isNaN(timestamp)) {
                    return new Date(timestamp);
                }
            } catch (err) {
                console.error('日期解析错误:', err);
            }
        }
        return new Date();
    }, [dateParam]); // 只有在dateParam改变时才重新计算

    // 考勤状态和对话框状态
    const [attendance, setAttendance] = useState<Attendance | null>(null);
    // 项目ID：优先来自URL参数，若未携带则从考勤记录推导
    const [programId, setProgramId] = useState<string | null>(programIdParam);
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [dialogState, setDialogState] = useState<{
        isOpen: boolean;
        title: string;
        action: 'signIn' | 'signOut' | null;
        studentId: string | null;
    }>({
        isOpen: false,
        title: '',
        action: null,
        studentId: null
    });
    
    // 批量签到对话框状态
    const [batchDialogOpen, setBatchDialogOpen] = useState(false);

    const { students, course, loading, error } = useStudentsList(courseId, programId);

    // 格式化日期显示 - 使用useMemo避免每次渲染都重新格式化
    const formattedDate = useMemo(() => {
        return selectedDate.toLocaleDateString(i18n.language === 'zh' ? 'zh-CN' : 'en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'long'
        });
    }, [selectedDate, i18n.language]);

    // 获取考勤记录
    useEffect(() => {
        const fetchAttendance = async () => {
            if (!attendanceId) return;

            try {
                setIsLoading(true);
                const result = await getAttendance(attendanceId);
                setAttendance(result);
                // 若URL未携带项目ID，则从考勤记录中推导，用于学生名单筛选
                if (result?.programId) {
                    setProgramId(prev => prev || result.programId);
                }
            } catch (error) {
                console.error('获取考勤记录失败:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAttendance();
    }, [attendanceId]);

    // 导出CSV
    const handleExportCsv = async () => {
        if (!attendance || !course || !students.length) return;

        try {
            setIsExporting(true);

            // 创建CSV内容
            const headers = [
                'studentName',
                'signInTime',
                'signInBy',
                'signOutTime',
                'signOutBy',
                'status'
            ];

            // 准备每个学生的考勤数据
            const rows = students.map(student => {
                const record = attendance.records[student.id];
                const studentName = `${student.firstName} ${student.lastName}`;
                const signInTime = record?.signInTime ? new Date(record.signInTime).toLocaleTimeString() : '';
                const signInBy = record?.signInName || '';
                const signOutTime = record?.signOutTime ? new Date(record.signOutTime).toLocaleTimeString() : '';
                const signOutBy = record?.signOutName || '';

                // 确定状态
                let status = '';
                if (record?.signInTime && record?.signOutTime) {
                    status = t('attendance.detail.status.present');
                } else if (record?.signInTime) {
                    status = t('attendance.detail.status.early');
                } else {
                    status = t('attendance.detail.status.absent');
                }

                return [studentName, signInTime, signInBy, signOutTime, signOutBy, status];
            });

            // 合并所有行
            const csvContent = [
                headers.join(','),
                ...rows.map(row => row.join(','))
            ].join('\n');

            // 创建Blob对象
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

            // 创建下载链接
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            // 设置文件名: 课程名-日期-attendance.csv
            const courseName = course.nameEn;
            const dateString = selectedDate.toISOString().split('T')[0];
            const fileName = `${courseName}-${dateString}-attendance.csv`;

            link.setAttribute('href', url);
            link.setAttribute('download', fileName);
            link.style.visibility = 'hidden';

            // 添加到DOM，点击并移除
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (error) {
            console.error('导出CSV失败:', error);
        } finally {
            setIsExporting(false);
        }
    };

    // 处理签到操作
    const handleSignIn = (studentId: string) => {
        setDialogState({
            isOpen: true,
            title: t('attendance.signInDialogTitle'),
            action: 'signIn',
            studentId
        });
    };

    // 处理签退操作
    const handleSignOut = (studentId: string) => {
        setDialogState({
            isOpen: true,
            title: t('attendance.signOutDialogTitle'),
            action: 'signOut',
            studentId
        });
    };

    // 处理批量签到操作
    const handleBatchSignIn = () => {
        setBatchDialogOpen(true);
    };

    // 关闭对话框
    const handleCloseDialog = () => {
        setDialogState(prev => ({ ...prev, isOpen: false }));
    };

    // 确认签到/签退
    const handleConfirmAttendance = async (name: string) => {
        if (!attendance || !dialogState.action || !dialogState.studentId) return;

        setIsLoading(true);
        try {
            if (dialogState.action === 'signIn') {
                await updateStudentSignIn(attendance.id, dialogState.studentId, name);
            } else if (dialogState.action === 'signOut') {
                await updateStudentSignOut(attendance.id, dialogState.studentId, name);
            }

            // 重新获取考勤数据
            const updatedAttendance = await getAttendance(attendanceId!);
            setAttendance(updatedAttendance);
        } catch (error) {
            console.error('更新考勤状态失败:', error);
        } finally {
            setDialogState(prev => ({ ...prev, isOpen: false }));
            setIsLoading(false);
        }
    };

    // 确认批量签到
    const handleConfirmBatchSignIn = async (name: string, selectedStudentIds: string[]) => {
        if (!attendance || selectedStudentIds.length === 0) return;

        setIsLoading(true);
        try {
            await batchSignInStudents(attendance.id, name, selectedStudentIds);

            // 重新获取考勤数据
            const updatedAttendance = await getAttendance(attendanceId!);
            setAttendance(updatedAttendance);
        } catch (error) {
            console.error('批量签到失败:', error);
        } finally {
            setBatchDialogOpen(false);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // 设置透明背景和白色文字
        setHeaderTheme({
            bgColor: 'bg-transparent',
            textColor: 'text-white'
        });

        return () => {
            // 恢复默认样式
            setHeaderTheme({
                bgColor: 'bg-white',
                textColor: 'text-[#142b89]'
            });
        };
    }, [setHeaderTheme]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-800 to-blue-600">
            {/* 顶部背景 */}
            <div className="relative w-full overflow-hidden">
                {/* 内容层 */}
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 z-10">
                    <div className="flex items-center mb-6">
                        <button
                            onClick={() => navigate('/teacher/courses')}
                            className="text-blue-200 hover:text-white mr-4"
                        >
                            <ArrowLeftIcon className="h-5 w-5" />
                        </button>
                        {course ? <motion.h1
                            className="text-3xl font-bold text-white flex-grow"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            {i18n.language === 'zh' ? course.name : course.nameEn}
                        </motion.h1> : <motion.h1
                            className="text-3xl font-bold text-white flex-grow"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            {t('course.loading')}
                        </motion.h1>}

                        {/* 批量签到按钮 */}
                        {attendance && students.length > 0 && (
                            <motion.button
                                onClick={handleBatchSignIn}
                                className="ml-4 bg-green-500 hover:bg-green-600 text-white rounded-full p-2 flex items-center justify-center transition-colors"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.2 }}
                                title={t('attendance.batchSignIn')}
                            >
                                <UserIcon className="h-5 w-5" />
                            </motion.button>
                        )}
                        
                        {/* 导出CSV按钮 - 移至标题行 */}
                        {attendance && (
                            <motion.button
                                onClick={handleExportCsv}
                                disabled={isExporting || !students.length}
                                className="ml-4 bg-blue-500 hover:bg-blue-600 text-white rounded-full p-2 flex items-center justify-center transition-colors disabled:bg-blue-400/50 disabled:cursor-not-allowed export-tooltip-container"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.3 }}
                            >
                                <DocumentArrowDownIcon className="h-5 w-5" />
                                <span className="export-tooltip-text">{isExporting ? t('common.exporting') : t('attendance.exportCsv')}</span>
                            </motion.button>
                        )}
                    </div>

                    <motion.div
                        className="text-blue-200"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                    >
                        <div className="text-lg mb-2">
                            {formattedDate}
                        </div>

                        {!loading && !error && (
                            <p className="mt-2">
                                {t('teacher.totalStudents')}: {students.length}
                            </p>
                        )}
                    </motion.div>
                </div>
            </div>

            {/* 主要内容区域 */}
            <div className="relative pb-20 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
                    {/* 移除原来的导出CSV按钮 */}

                    {loading ? (
                        <div className="p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                            <div className="flex justify-center items-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-transparent"></div>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                            <div className="text-center py-8">
                                <p className="text-red-300">{error}</p>
                            </div>
                        </div>
                    ) : students.length === 0 ? (
                        <div className="p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                            <div className="text-center py-8">
                                <p className="text-blue-100">{t('teacher.noStudentsInCourse')}</p>
                            </div>
                        </div>
                    ) : !attendance ? (
                        <div className="p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                            <div className="text-center py-8">
                                <p className="text-blue-100">{t('attendance.noRecords')}</p>
                                <p className="mt-2 text-blue-200">{t('attendance.goBack')}</p>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {students.map((student) => (
                                <StudentAttendanceCard
                                    key={student.id}
                                    student={student}
                                    attendanceRecord={attendance?.records[student.id]}
                                    onSignIn={handleSignIn}
                                    onSignOut={handleSignOut}
                                    isAttendanceEnabled={Boolean(attendance)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* 签到/签退对话框 */}
            <AttendanceDialog
                isOpen={dialogState.isOpen}
                title={dialogState.title}
                onClose={handleCloseDialog}
                onConfirm={handleConfirmAttendance}
                isLoading={isLoading}
            />

            {/* 批量签到对话框 */}
            <AttendanceBatchDialog
                isOpen={batchDialogOpen}
                title={t('attendance.batchSignInDialogTitle')}
                students={students}
                attendanceRecords={attendance?.records || {}}
                onClose={() => setBatchDialogOpen(false)}
                onConfirm={handleConfirmBatchSignIn}
                isLoading={isLoading}
            />
        </div>
    );
};

export default AttendancePage; 